<template>
  <div class="collections-page">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>{{ $t('menu.translations.subpages.collections.header') }}</h1>
        <p class="page-subtitle">{{ $t('menu.translations.subpages.collections.subtitle', { count: user.collections.length }) }}</p>
      </div>
      <button class="appButton" @click="$router.push('/translations')">
        <i class="bx bx-arrow-back" style="margin-right: 0.25rem;"></i>
        {{ $t('menu.settings.back') }}
      </button>
    </div>

    <div class="collections-layout">
      <!-- Left Column: Collection List -->
      <div class="collections-sidebar">
        <h3>{{ $t('menu.translations.subpages.collections.sidebarTitle') }}</h3>
        <ul class="collection-list">
          <li v-for="collection in user.collections" :key="collection.id"
            class="collection-item"
            :class="{ 'collection-item--active': collection.id === user.selectedCollectionId }"
            @click="user.mergeDatabaseTranslations(collection.id, collection.idNumber, 'Collection')">
            {{ collection.title }}
          </li>
        </ul>
      </div>

      <!-- Right Column: Translation Cards -->
      <div class="collections-content">

        <!-- Default Language (English) -->
        <div class="lang-card lang-card--primary">
          <div class="lang-card-header">
            <span class="lang-badge lang-badge--primary">{{ $t('common.defaultLanguage') }}</span>
          </div>
          <div class="lang-card-body">
            <div class="lang-fields-row">
              <div class="lang-field-group">
                <label>{{ $t('common.title') }}</label>
                <input v-model="user.translations.en.title" type="text" />
                <label class="mt-3">{{ $t('common.description') }}</label>
                <textarea v-model="user.translations.en.description" rows="4"></textarea>
                <div class="lang-actions">
                  <button v-if="user.userRoleId < 2" class="appButton mt-3" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                  <button v-if="user.userRoleId < 2" class="appButton mt-3" @click="updateShopify('en', user.translations.en)">{{ $t('menu.settings.updateShopify') }} (ENG)</button>
                </div>
              </div>
              <div class="lang-field-group">
                <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.en.meta_title) }}</span></label>
                <input v-model="user.translations.en.meta_title" type="text" />
                <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.en.meta_description) }}</span></label>
                <textarea v-model="user.translations.en.meta_description" rows="4"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Language Cards Grid -->
        <div class="lang-grid">

          <!-- German -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/de.png" alt="German Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.german') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.de.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.de.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.de.meta_title) }}</span></label>
                  <input v-model="user.translations.de.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.de.meta_description) }}</span></label>
                  <textarea v-model="user.translations.de.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('de')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('de', user.translations.de)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('de', user.translations.de)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Spanish -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/es.png" alt="Spanish Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.spanish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.es.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.es.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.es.meta_title) }}</span></label>
                  <input v-model="user.translations.es.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.es.meta_description) }}</span></label>
                  <textarea v-model="user.translations.es.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('es')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('es', user.translations.es)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('es', user.translations.es)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Danish -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/da.png" alt="Danish Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.danish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.da.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.da.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.da.meta_title) }}</span></label>
                  <input v-model="user.translations.da.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.da.meta_description) }}</span></label>
                  <textarea v-model="user.translations.da.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('da')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('da', user.translations.da)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('da', user.translations.da)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Dutch -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/nl.png" alt="Dutch Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.dutch') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.nl.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.nl.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.nl.meta_title) }}</span></label>
                  <input v-model="user.translations.nl.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.nl.meta_description) }}</span></label>
                  <textarea v-model="user.translations.nl.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('nl')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('nl', user.translations.nl)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('nl', user.translations.nl)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- French -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/fr.png" alt="French Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.french') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.fr.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.fr.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.fr.meta_title) }}</span></label>
                  <input v-model="user.translations.fr.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.fr.meta_description) }}</span></label>
                  <textarea v-model="user.translations.fr.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('fr')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('fr', user.translations.fr)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('fr', user.translations.fr)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- Swedish -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/sv.png" alt="Swedish Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.swedish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.sv.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.sv.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.sv.meta_title) }}</span></label>
                  <input v-model="user.translations.sv.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.sv.meta_description) }}</span></label>
                  <textarea v-model="user.translations.sv.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('sv')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('sv', user.translations.sv)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('sv', user.translations.sv)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

          <!-- UK -->
          <div class="lang-card">
            <div class="lang-card-header">
              <img width="24" src="@/assets/img/flags/uk.png" alt="UK Flag" class="lang-flag" />
              <span class="lang-name">{{ $t('languages.britishEnglish') }}</span>
            </div>
            <div class="lang-card-body">
              <div class="lang-fields-row">
                <div class="lang-field-group">
                  <label>{{ $t('common.title') }}</label>
                  <input v-model="user.translations.gb.title" type="text" />
                  <label class="mt-3">{{ $t('common.description') }}</label>
                  <textarea v-model="user.translations.gb.description" rows="4"></textarea>
                </div>
                <div class="lang-field-group">
                  <label>{{ $t('common.seoTitle') }} <span class="seo-counter">{{ validateTitleLength(user.translations.gb.meta_title) }}</span></label>
                  <input v-model="user.translations.gb.meta_title" type="text" />
                  <label class="mt-3">{{ $t('common.seoDescription') }} <span class="seo-counter">{{ validateDescriptionLength(user.translations.gb.meta_description) }}</span></label>
                  <textarea v-model="user.translations.gb.meta_description" rows="4"></textarea>
                </div>
              </div>
              <div class="lang-actions">
                <button class="appButton" @click="user.copyLocale('gb')">{{ $t('menu.settings.copy') }}</button>
                <button class="appButton" @click="updateLocale('gb', user.translations.gb)">{{ $t('menu.settings.update') }}</button>
                <button v-if="user.userRoleId < 2" class="appButton" @click="user.postToDatabase()">{{ $t('menu.settings.storeToDb') }}</button>
                <button class="appButton" @click="updateShopify('gb', user.translations.gb)">{{ $t('menu.settings.updateShopify') }}</button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSettings } from '@/store/user'
import mixins from '@/components/mixins';

export default {
  mixins: [mixins],
  setup() {
    const user = useSettings();
    user.getCollections();

    return { user }
  },
  data() {
    return {
      collectionHandleData: '',
      digests: []
    }
  },
  methods: {
    async updateLocale(locale) {
      const fields = ['meta_title', 'meta_description'];

      for (const lang in this.user.translations) {
        if (lang === 'en') continue;
        if (lang !== locale) continue;

        for (const field of fields) {
          if (this.user.translations[lang][field] === '') continue;

          const res = await this.user.getTranslations({
            locale: locale,
            text: this.user.translations[locale][field],
          });

          this.user.translations[locale][field] = res.translation;
        }
      }
    },
    async updateShopify(locale, langObj) {
      const fields = ['meta_title','meta_description'];
      const resourceId = this.user.selectedCollectionId;

      for (const field of fields) {
        if (langObj[field] === '') continue;

        let key = '';
        let type = '';
        let mutation = '';
        let title = null;

        const escapedValue = await this.escapeGraphQLString(langObj[field]);

        if(locale === 'en') {
          if (field === 'title') key = 'title', type = 'single_line_text_field';
          if (field === 'description') key = 'body_html',  type = 'multi_line_text_field';
          if (field === 'meta_title') key = 'title_tag', type = 'single_line_text_field';
          if (field === 'meta_description') key = 'description_tag', type = 'single_line_text_field';

          mutation = `
            mutation {
              collectionUpdate(
                input: {
                  id: "${resourceId}",
                  metafields: [
                    {
                      namespace: "global",
                      key: "${key}",
                      type: "${type}",
                      value: "${escapedValue}"
                    }
                  ]
                }
              ) {
                collection {
                  id
                }
                userErrors {
                  field
                  message
                }
              }
            }
          `;

        } else {
          if (field === 'title') key = 'title', type = 'single_line_text_field';
          if (field === 'description') key = 'body_html', type = 'multi_line_text_field';
          if (field === 'meta_title') key = 'meta_title', type = 'single_line_text_field';
          if (field === 'meta_description') key = 'meta_description', type = 'single_line_text_field';

          const digest = this.user.digests.find(d => d.key === key)?.digest || '';

          mutation = `
            mutation {
              translationsRegister(
                resourceId: "${resourceId}",
                translations: [
                  {
                    locale: "${locale}",
                    key: "${key}",
                    value: "${escapedValue}",
                    translatableContentDigest: "${digest}"
                  }
                ]
              ) {
                userErrors {
                  field
                  message
                }
              }
            }
          `;
        }

        try {
          const res = await this.user.saveTranslations(mutation);

        } catch (error) {
          console.error('Fejl ved opdatering:', error.response?.data || error.message);
        }
      }
    },
    async escapeGraphQLString(str) {
      return str
          .replace(/\\/g, '\\\\')
          .replace(/"""/g, '\\"""')
          .replace(/"/g, '\\"');
    },
    validateTitleLength(field) {
      if (!field) return '0';
      if (field.length > 70) {
        return `Too long by ${field.length - 70} characters`;
      } else {
        return `${field.length}/70 characters`;
      }
    },
    validateDescriptionLength(field) {
      if (!field) return '0';
      if (field.length > 160) {
        return `Too long by ${field.length - 160} characters`;
      } else {
        return `${field.length}/160 characters`;
      }
    },
    async copyAll() {
      const fields = ['meta_title', 'meta_description'];

      for (const lang in this.user.translations) {
        if (lang === 'en') continue;

        fields.forEach(field => {
          this.user.translations[lang][field] = this.user.translations['en'][field];
        });
      }
    }
  },
}
</script>

<style scoped>
/* Layout */
.collections-page {
  animation: fadeIn 0.3s ease-out;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}
.page-subtitle {
  color: #9aa0a8;
  font-size: 0.875rem;
  margin-top: 0.125rem;
}

.collections-layout {
  display: flex;
  gap: 1.5rem;
  min-height: calc(100vh - 180px);
}

/* Sidebar */
.collections-sidebar {
  width: 240px;
  flex-shrink: 0;
  background: #1a2028;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 1.25rem;
  overflow-y: auto;
}
.collections-sidebar h3 {
  margin-bottom: 0.75rem;
}
.collection-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.collection-item {
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #9aa0a8;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.collection-item:hover {
  background: rgba(255, 255, 255, 0.04);
  color: #e8eaed;
}
.collection-item--active {
  background: rgba(117, 35, 35, 0.1);
  color: #e8eaed;
  border-left: 3px solid #a63c3c;
  padding-left: calc(0.75rem - 3px);
}

/* Content area */
.collections-content {
  flex: 1;
  min-width: 0;
}

/* Language cards */
.lang-card {
  background: #1a2028;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.15);
}
.lang-card--primary {
  border-color: rgba(117, 35, 35, 0.2);
}
.lang-card--primary::before {
  content: '';
  display: block;
  height: 2px;
  background: linear-gradient(90deg, #752323, #a63c3c, #c94a4a);
}

.lang-card-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.lang-flag {
  border-radius: 3px;
}
.lang-name {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  color: #e8eaed;
}
.lang-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
}
.lang-badge--primary {
  background: rgba(117, 35, 35, 0.15);
  color: #c94a4a;
  border: 1px solid rgba(117, 35, 35, 0.2);
}

.lang-card-body {
  padding: 1.25rem;
}

.lang-fields-row {
  display: flex;
  gap: 1.25rem;
}
.lang-field-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.lang-field-group label {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}
.lang-field-group input,
.lang-field-group textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
}
.lang-field-group textarea {
  resize: vertical;
}

.mt-3 {
  margin-top: 0.75rem;
}

.seo-counter {
  font-size: 0.625rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #5c6370;
}

.lang-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

/* Language grid for non-primary cards */
.lang-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(560px, 1fr));
  gap: 1rem;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
